import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { screensData } from '../../data/screensData';
import { transducersData } from '../../data/transducersData';
import { batteriesData } from '../../data/batteriesData';
import { shuttlesData } from '../../data/shuttlesData';
import { polesData } from '../../data/polesData';
import { fetchLowestPrice, fetchProductNamesWithOffers, resolveProductNameForSupabase } from '../../utils/api';
import { FaFacebook, FaTwitter, FaSms } from 'react-icons/fa';
import './PriceComparisonPage.css';


const PriceComparisonLanding = () => {
  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular'); // Change default to popular
  const [popularityData, setPopularityData] = useState({});
  const [isLoadingPopularity, setIsLoadingPopularity] = useState(true);
  const [lowestPrices, setLowestPrices] = useState({});
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [productNamesWithOffers, setProductNamesWithOffers] = useState(null);

  // Load popularity data and product-names-with-offers from server on component mount
  useEffect(() => {
    fetchPopularityData();
    fetchProductNamesWithOffers().then(setProductNamesWithOffers);
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if a product is from Amped Outdoors (for discount calculation)
  const isAmpedOutdoorsProduct = (product) => {
    return product.category === 'Batteries' && 
           product.name && 
           product.name.toLowerCase().includes('amped outdoors');
  };

  // Function to handle copy link to clipboard
  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000); // Hide toast after 3 seconds
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  // Function to generate QR code URL
  const generateQRCodeUrl = () => {
    const currentUrl = encodeURIComponent(window.location.href);
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${currentUrl}`;
  };

  // Fetch lowest price for a product
  const fetchProductLowestPrice = async (product) => {
    if (!product?.csvLookupId || lowestPrices[product.id]) {
      return;
    }

    try {
      const isAmped = isAmpedOutdoorsProduct(product);
      console.log(`🚀 Fetching price for: ${product.name} (CSV: ${product.csvLookupId}) with MSRP $${product.msrp} (Amped Outdoors: ${isAmped})`);
      
      const lowestPriceData = await fetchLowestPrice(
        product.csvLookupId, 
        true, // exact match only
        product.msrp, 
        isAmped
      );
      
      if (lowestPriceData && lowestPriceData.success) {
        console.log(`✅ Price fetched for ${product.name}: $${lowestPriceData.price}`);
        setLowestPrices(prev => ({
          ...prev,
          [product.id]: lowestPriceData
        }));
      }
    } catch (error) {
      console.error(`Error fetching lowest price for ${product.name}:`, error);
    }
  };

  // Fetch popularity data from server
  const fetchPopularityData = async () => {
    try {
      setIsLoadingPopularity(true);
      const response = await fetch('/api/popularity');
      if (response.ok) {
        const data = await response.json();
        setPopularityData(data);
      } else {
        console.error('Failed to fetch popularity data');
      }
    } catch (error) {
      console.error('Error fetching popularity data:', error);
    } finally {
      setIsLoadingPopularity(false);
    }
  };

  // Combine all product data with category information
  const allProducts = useMemo(() => {
    const products = [];
    
    // Add screens
    screensData.forEach(product => {
      products.push({
        ...product,
        category: 'Screens',
        categorySlug: 'screen',
        priceComparisonUrl: `/simple-products/${product.id}`,
        searchTerms: `${product.name} ${product.screenSize}" screen fish finder display`.toLowerCase()
      });
    });
    
    // Add transducers
    transducersData.forEach(product => {
      products.push({
        ...product,
        category: product.type === 'livescope' ? 'Live Imaging' : 
                 product.type === 'openWater' ? 'Open Water Sonar' : 'Ice Fishing',
        categorySlug: 'transducer',
        priceComparisonUrl: `/simple-products/${product.id}`,
        searchTerms: `${product.name} ${product.type} transducer sonar`.toLowerCase()
      });
    });
    
    // Add batteries
    batteriesData.forEach(product => {
      // Only add main battery product if it has its own csvLookupId
      if (product.csvLookupId) {
        products.push({
          ...product,
          category: 'Batteries',
          categorySlug: 'battery',
          priceComparisonUrl: `/simple-products/${product.id}`,
          searchTerms: `${product.name} ${product.capacity} ${product.type} battery power`.toLowerCase()
        });
      }
      
      // Add battery variants if they exist
      if (product.variants) {
        Object.values(product.variants).forEach(variant => {
          if (variant.csvLookupId) {
            products.push({
              ...variant,
              category: 'Batteries',
              categorySlug: 'battery',
              priceComparisonUrl: `/simple-products/${variant.id}`,
              searchTerms: `${variant.name} ${product.capacity} ${product.type} battery power`.toLowerCase(),
              // Copy over important properties from main product
              capacity: product.capacity,
              type: product.type,
              dealInfo: product.dealInfo,
              image: product.image,
              fallbackImage: product.fallbackImage
            });
          }
        });
      }
    });
    
    // Add shuttles
    shuttlesData.forEach(product => {
      products.push({
        ...product,
        category: 'Shuttles & Cases',
        categorySlug: 'shuttle',
        priceComparisonUrl: `/simple-products/${product.id}`,
        searchTerms: `${product.name} shuttle case portable power`.toLowerCase()
      });
    });
    
    // Add poles (exclude selector/configurator poles that don't have their own pricing)
    polesData.forEach(product => {
      // Only add poles that have csvLookupId (actual products with pricing)
      // Exclude selector/configurator poles like beatdown-pole-selector
      if (product.csvLookupId) {
        products.push({
          ...product,
          category: 'Pole Mounts',
          categorySlug: 'pole',
          priceComparisonUrl: `/simple-products/${product.id}`,
          searchTerms: `${product.name} pole mount transducer`.toLowerCase()
        });
      }
    });
    
    return products;
  }, []);

  // Fetch lowest prices for products with csvLookupId
  useEffect(() => {
    if (isFetchingPrices) return;

    const productsWithPricing = allProducts.filter(product => 
      product.csvLookupId && !lowestPrices[product.id]
    );

    if (productsWithPricing.length > 0) {
      setIsFetchingPrices(true);
      console.log(`🔍 Starting price fetch for ${productsWithPricing.length} products`);
      
      // Stagger the price fetching to reduce server load
      productsWithPricing.forEach((product, index) => {
        setTimeout(() => {
          fetchProductLowestPrice(product);
        }, index * 150); // 150ms delay between each request
      });

      // Reset fetching flag after all requests are initiated
      setTimeout(() => {
        setIsFetchingPrices(false);
      }, productsWithPricing.length * 150 + 1000);
    }
  }, [allProducts, lowestPrices, isFetchingPrices]);

  // Track clicks on product links by sending to server
  const trackProductClick = async (productId, productName, category) => {
    try {
      const response = await fetch('/api/track-click', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId,
          productName,
          category
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`Tracked click for ${productName}: ${result.totalClicks} total clicks`);
        
        // Update local popularity data
        setPopularityData(prev => ({
          ...prev,
          [productId]: {
            ...prev[productId],
            count: result.totalClicks,
            productName,
            category,
            lastClicked: new Date().toISOString()
          }
        }));
      } else {
        console.error('Failed to track click');
      }
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  // Filter and sort products (only show cards for products that have at least one active offer)
  const filteredProducts = useMemo(() => {
    let filtered = allProducts;

    // Hide cards for products with no active offers (e.g. Amped-only after retailer deactivation)
    if (productNamesWithOffers instanceof Set && productNamesWithOffers.size > 0) {
      filtered = filtered.filter((product) => {
        if (!product.csvLookupId) return true;
        const resolvedName = resolveProductNameForSupabase(product.csvLookupId);
        return productNamesWithOffers.has(resolvedName);
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.searchTerms.includes(searchTerm.toLowerCase()) ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    // Sort products
    const clickCounts = popularityData;
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return (a.msrp || 0) - (b.msrp || 0);
        case 'category':
          return a.category.localeCompare(b.category) || a.name.localeCompare(b.name);
        case 'popular':
          // Sort by click count (descending), then by name
          const aClicks = clickCounts[a.id]?.count || 0;
          const bClicks = clickCounts[b.id]?.count || 0;
          if (aClicks !== bClicks) {
            return bClicks - aClicks; // Higher clicks first
          }
          return a.name.localeCompare(b.name);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });
    
    return filtered;
  }, [allProducts, searchTerm, selectedCategory, sortBy, popularityData, productNamesWithOffers]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(allProducts.map(p => p.category))];
    return cats.sort();
  }, [allProducts]);

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleImageError = (e, product) => {
    e.target.onerror = null;
    if (product.fallbackImage) {
      e.target.src = product.fallbackImage;
    } else {
      e.target.src = `https://dummyimage.com/200x150/e0e0e0/666666&text=${encodeURIComponent(product.name.split(' ').slice(0, 2).join(' '))}`;
    }
  };

  return (
    <div style={{ 
      backgroundColor: theme.colors.background, 
      minHeight: '100vh', 
      color: theme.colors.text 
    }}>
      {/* Compact Mobile-First Header */}
      <div style={{
        background: theme.isDarkMode ? 
          'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' : 
          'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: window.innerWidth <= 768 ? '20px 15px 15px 15px' : '40px 20px 30px 20px'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Compact Title */}
          <h1 style={{
            fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.5rem',
            fontWeight: '700',
            marginBottom: window.innerWidth <= 768 ? '8px' : '16px',
            textAlign: 'center',
            background: theme.isDarkMode ? 
              'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)' :
              'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Price Comparison Hub
          </h1>
          
          {/* Shorter description on mobile */}
          {window.innerWidth <= 768 ? (
            <p style={{
              fontSize: '0.9rem',
              color: theme.isDarkMode ? '#b0b0b0' : '#666',
              textAlign: 'center',
              margin: '0 auto 20px auto',
              lineHeight: '1.4'
            }}>
              Compare prices on fishing electronics from top retailers
            </p>
          ) : (
            <p style={{
              fontSize: '1.1rem',
              color: theme.isDarkMode ? '#b0b0b0' : '#666',
              maxWidth: '600px',
              margin: '0 auto 30px auto',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Find the best deals on fish finders, transducers, batteries, and accessories. 
              Compare real-time prices from top retailers instantly.
            </p>
          )}
          
          {/* Compact Search and Filter Controls */}
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            gap: window.innerWidth <= 768 ? '10px' : '15px',
            maxWidth: '800px',
            margin: '0 auto',
            alignItems: 'center'
          }}>
            {/* Compact Search Bar */}
            <div style={{ 
              position: 'relative', 
              flex: window.innerWidth <= 768 ? 'none' : '1',
              width: window.innerWidth <= 768 ? '100%' : 'auto'
            }}>
              <input
                type="text"
                placeholder={window.innerWidth <= 768 ? "Search products..." : "Search for fish finders, transducers, batteries..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="price-comparison-search-input"
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '12px 40px 12px 15px' : '15px 50px 15px 20px',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: theme.isDarkMode ? '#ffffff' : '#000000',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: theme.isDarkMode ? 
                    '0 2px 8px rgba(0,0,0,0.2)' : 
                    '0 2px 8px rgba(0,0,0,0.1)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3498db';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.colors.border;
                }}
              />
              <svg 
                style={{
                  position: 'absolute',
                  right: window.innerWidth <= 768 ? '12px' : '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: window.innerWidth <= 768 ? '16px' : '20px',
                  height: window.innerWidth <= 768 ? '16px' : '20px',
                  color: theme.isDarkMode ? '#888' : '#999'
                }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Compact Filter Controls */}
            <div style={{
              display: 'flex',
              gap: window.innerWidth <= 768 ? '8px' : '15px',
              width: window.innerWidth <= 768 ? '100%' : 'auto'
            }}>
              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                  fontSize: window.innerWidth <= 768 ? '13px' : '15px',
                  fontWeight: '500',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: theme.isDarkMode ? '#ffffff' : '#000000',
                  outline: 'none',
                  cursor: 'pointer',
                  flex: window.innerWidth <= 768 ? '1' : 'none',
                  minWidth: window.innerWidth <= 768 ? '0' : '140px',
                  transition: 'all 0.2s ease',
                  appearance: 'none',
                  backgroundImage: theme.isDarkMode ? 
                    'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'><path fill=\'%23ffffff\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/></svg>")' :
                    'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'><path fill=\'%23000000\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/></svg>")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '10px',
                  paddingRight: window.innerWidth <= 768 ? '25px' : '30px'
                }}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              
              {/* Sort Options */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                  fontSize: window.innerWidth <= 768 ? '13px' : '15px',
                  fontWeight: '500',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: theme.isDarkMode ? '#ffffff' : '#000000',
                  outline: 'none',
                  cursor: 'pointer',
                  flex: window.innerWidth <= 768 ? '1' : 'none',
                  minWidth: window.innerWidth <= 768 ? '0' : '140px',
                  transition: 'all 0.2s ease',
                  appearance: 'none',
                  backgroundImage: theme.isDarkMode ? 
                    'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'><path fill=\'%23ffffff\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/></svg>")' :
                    'url("data:image/svg+xml;charset=US-ASCII,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 4 5\'><path fill=\'%23000000\' d=\'M2 0L0 2h4zm0 5L0 3h4z\'/></svg>")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '10px',
                  paddingRight: window.innerWidth <= 768 ? '25px' : '30px'
                }}
              >
                <option value="name">Sort by Name</option>
                <option value="popular">Sort by Popular</option>
                <option value="price">Sort by Price</option>
                <option value="category">Sort by Category</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products Preview - Mobile First */}
      {window.innerWidth <= 768 && (
        <div style={{
          padding: '20px 15px',
          borderBottom: `1px solid ${theme.colors.border}`,
          backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#f8f9fa',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <h2 style={{
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: '0 0 15px 0',
            textAlign: 'center',
            color: theme.colors.text
          }}>
            Popular Products
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 400 ? '1fr' : 'repeat(2, 1fr)',
            gap: window.innerWidth <= 400 ? '12px' : '6px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {filteredProducts.slice(0, 4).map((product) => (
              <Link
                key={`preview-${product.categorySlug}-${product.id}`}
                to={product.priceComparisonUrl}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                onClick={() => trackProductClick(product.id, product.name, product.category)}
              >
                                 <div style={{
                   backgroundColor: theme.colors.surface,
                   borderRadius: '8px',
                   border: `1px solid ${theme.colors.border}`,
                   overflow: 'hidden',
                   transition: 'all 0.2s ease',
                   cursor: 'pointer',
                   width: '100%',
                   boxSizing: 'border-box'
                 }}>
                                     {/* Compact Product Image */}
                   <div style={{
                     height: window.innerWidth <= 400 ? '100px' : '90px',
                     backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#ffffff',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     position: 'relative'
                   }}>
                    <img
                      src={product.image}
                      alt={product.name}
                      onError={(e) => handleImageError(e, product)}
                      style={{
                        maxWidth: '85%',
                        maxHeight: '85%',
                        objectFit: 'contain'
                      }}
                    />
                    {/* Compact Category Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '6px',
                      left: '6px',
                      backgroundColor: theme.isDarkMode ? 'rgba(52,152,219,0.9)' : '#3498db',
                      color: 'white',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      {product.category}
                    </div>
                  </div>
                  
                                     {/* Product Info */}
                   <div style={{ padding: window.innerWidth <= 400 ? '8px 6px' : '6px 5px' }}>
                                         <h3 style={{
                       fontSize: window.innerWidth <= 400 ? '10px' : '9px',
                       fontWeight: '600',
                       margin: '0 0 4px 0',
                       color: theme.colors.text,
                       lineHeight: '1.1',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis',
                       whiteSpace: 'nowrap'
                     }}>
                      {product.name}
                    </h3>
                    
                    {/* Price Info */}
                    <div style={{ marginTop: '5px' }}>
                      {lowestPrices[product.id] ? (
                        <div>
                                                                                <div style={{
                             fontSize: window.innerWidth <= 400 ? '11px' : '10px',
                             fontWeight: '700',
                             color: '#e74c3c'
                           }}>
                             From {formatPrice(lowestPrices[product.id].price)}
                           </div>
                          {product.msrp && lowestPrices[product.id].price < product.msrp && (
                                                                                      <div style={{
                               fontSize: window.innerWidth <= 400 ? '9px' : '8px',
                               color: theme.isDarkMode ? '#888' : '#666',
                               textDecoration: 'line-through'
                             }}>
                               MSRP {formatPrice(product.msrp)}
                             </div>
                          )}
                        </div>
                      ) : (
                                                 <div style={{
                           fontSize: window.innerWidth <= 400 ? '10px' : '9px',
                           color: theme.isDarkMode ? '#888' : '#666'
                         }}>
                          Loading price...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div style={{
            textAlign: 'center',
            marginTop: '15px'
          }}>
            <button
              onClick={() => {
                const resultsSection = document.querySelector('[data-results-section]');
                if (resultsSection) {
                  resultsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2980b9';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3498db';
              }}
            >
              View All {filteredProducts.length} Products
            </button>
          </div>
        </div>
      )}

      {/* Social Sharing Bar */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '20px',
        marginBottom: '20px',
        padding: '20px',
        borderRadius: '8px',
        background: theme.isDarkMode ? '#1c1c1c' : '#f5f5f5',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        maxWidth: '1200px',
        margin: '20px auto'
      }}>
        <p style={{
          margin: '0 0 12px 0',
          fontSize: '14px',
          fontWeight: '600',
          color: theme.isDarkMode ? '#e0e0e0' : '#333'
        }}>
          Share or Save This Price Comparison Tool:
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: window.innerWidth <= 768 ? '8px' : '16px',
          width: '100%',
          flexWrap: 'nowrap',
          overflow: 'hidden'
        }}>
          {/* Copy Link Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: window.innerWidth <= 768 ? '60px' : '70px',
            gap: '0'
          }}>
            <button
              onClick={handleCopyLink}
              aria-label="Copy link to clipboard"
              style={{
                backgroundColor: "#2c2c2c",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                marginBottom: "8px"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#222";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#2c2c2c";
              }}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
            </button>
            <span style={{
              fontSize: window.innerWidth <= 768 ? "10px" : "11px",
              marginTop: "0",
              padding: 0,
              margin: 0,
              lineHeight: "1",
              display: "block",
              position: "relative",
              top: "-4px",
              textAlign: "center"
            }}>
              Copy Link
            </span>
          </div>

          {/* QR Code Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: window.innerWidth <= 768 ? '60px' : '70px',
            gap: '0'
          }}>
            <button
              onClick={() => setShowQRCode(true)}
              aria-label="Generate QR Code"
              style={{
                backgroundColor: "#2c2c2c",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                marginBottom: "8px"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#222";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#2c2c2c";
              }}
            >
              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM19 13h2v2h-2zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM15 19h2v2h-2zM17 17h2v2h-2zM17 19h2v2h-2zM19 17h2v2h-2z"/>
              </svg>
            </button>
            <span style={{
              fontSize: window.innerWidth <= 768 ? "10px" : "11px",
              marginTop: "0",
              padding: 0,
              margin: 0,
              lineHeight: "1",
              display: "block",
              position: "relative",
              top: "-4px",
              textAlign: "center"
            }}>
              QR Code
            </span>
          </div>
          
          {/* SMS Share Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: window.innerWidth <= 768 ? '60px' : '70px',
            gap: '0'
          }}>
            <a
              href={`sms:?body=${encodeURIComponent(
                `Check out this price comparison tool: ${window.location.href}`
              )}`}
              aria-label="Share via SMS"
              style={{
                backgroundColor: "#2c2c2c",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                marginBottom: "8px"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#222";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#2c2c2c";
              }}
            >
              <FaSms size={18} />
            </a>
            <span style={{
              fontSize: window.innerWidth <= 768 ? "10px" : "11px",
              marginTop: "0",
              padding: 0,
              margin: 0,
              lineHeight: "1",
              display: "block",
              position: "relative",
              top: "-4px",
              textAlign: "center"
            }}>
              SMS
            </span>
          </div>

          {/* Facebook Share Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: window.innerWidth <= 768 ? '60px' : '70px',
            gap: '0'
          }}>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                window.location.href
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on Facebook"
              style={{
                backgroundColor: "#3b5998",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                marginBottom: "8px"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#2d4373";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#3b5998";
              }}
            >
              <FaFacebook size={18} />
            </a>
            <span style={{
              fontSize: window.innerWidth <= 768 ? "10px" : "11px",
              marginTop: "0",
              padding: 0,
              margin: 0,
              lineHeight: "1",
              display: "block",
              position: "relative",
              top: "-4px",
              textAlign: "center"
            }}>
              Facebook
            </span>
          </div>
          
          {/* Twitter Share Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: window.innerWidth <= 768 ? '60px' : '70px',
            gap: '0'
          }}>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                window.location.href
              )}&text=${encodeURIComponent(
                "Check out this awesome price comparison tool for fish finders and accessories!"
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on Twitter"
              style={{
                backgroundColor: "#1da1f2",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                marginBottom: "8px"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#0d8ddb";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#1da1f2";
              }}
            >
              <FaTwitter size={18} />
            </a>
            <span style={{
              fontSize: window.innerWidth <= 768 ? "10px" : "11px",
              marginTop: "0",
              padding: 0,
              margin: 0,
              lineHeight: "1",
              display: "block",
              position: "relative",
              top: "-4px",
              textAlign: "center"
            }}>
              Twitter
            </span>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div data-results-section style={{ padding: window.innerWidth <= 768 ? '20px 15px' : '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Results Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              margin: '0',
              color: theme.colors.text
            }}>
              {filteredProducts.length} Products Found
            </h2>
            {searchTerm && (
              <p style={{
                fontSize: '0.9rem',
                color: theme.isDarkMode ? '#888' : '#666',
                margin: '5px 0 0 0'
              }}>
                Results for "{searchTerm}"
              </p>
            )}
            {sortBy === 'popular' && (
              <p style={{
                fontSize: '0.9rem',
                color: theme.isDarkMode ? '#4fc3f7' : '#1976d2',
                margin: '5px 0 0 0',
                fontWeight: '500'
              }}>
                Sorted by popularity (most clicked first)
              </p>
            )}
          </div>
          
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '6px',
                color: theme.colors.text,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.isDarkMode ? '#333' : '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              Clear Search
            </button>
          )}
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: theme.isDarkMode ? '#888' : '#666'
          }}>
            <svg 
              style={{ width: '64px', height: '64px', marginBottom: '20px', opacity: 0.5 }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>No products found</h3>
            <p>Try adjusting your search terms or filter settings.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? 
              'repeat(auto-fill, minmax(280px, 1fr))' : 
              'repeat(auto-fill, minmax(300px, 1fr))',
            gap: window.innerWidth <= 768 ? '16px' : '24px'
          }}>
            {filteredProducts.map((product) => (
              <Link
                key={`${product.categorySlug}-${product.id}`}
                to={product.priceComparisonUrl}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                onClick={() => trackProductClick(product.id, product.name, product.category)}
              >
                <div style={{
                  backgroundColor: theme.colors.surface,
                  borderRadius: '12px',
                  border: `1px solid ${theme.colors.border}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: theme.isDarkMode ? 
                    '0 4px 12px rgba(0,0,0,0.3)' : 
                    '0 4px 12px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = theme.isDarkMode ? 
                    '0 8px 25px rgba(0,0,0,0.4)' : 
                    '0 8px 25px rgba(0,0,0,0.15)';
                  e.currentTarget.style.borderColor = '#3498db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.isDarkMode ? 
                    '0 4px 12px rgba(0,0,0,0.3)' : 
                    '0 4px 12px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
                >
                  {/* Product Image */}
                  <div style={{
                    height: '200px',
                    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={product.image}
                      alt={product.name}
                      onError={(e) => handleImageError(e, product)}
                      style={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        objectFit: 'contain'
                      }}
                    />
                    
                    {/* Category Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      backgroundColor: theme.isDarkMode ? 'rgba(52,152,219,0.9)' : '#3498db',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {product.category}
                    </div>
                    
                    {/* Popular Badge */}
                    {(() => {
                      const clickCounts = popularityData[product.id] || {};
                      const clickCount = clickCounts.count || 0;
                      const isPopular = clickCount >= 5; // Popular if 5+ clicks
                      
                      if (isPopular) {
                        return (
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            backgroundColor: '#ff6b35',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '700',
                            transform: 'translateY(-28px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            🔥 Popular
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Price Comparison Icon */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      backgroundColor: theme.isDarkMode ? 'rgba(46,204,113,0.9)' : '#2ecc71',
                      color: 'white',
                      padding: '6px',
                      borderRadius: '50%',
                      fontSize: '12px'
                    }}>
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                        <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                      </svg>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      margin: '0 0 8px 0',
                      color: theme.colors.text,
                      lineHeight: '1.3',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {product.name}
                    </h3>
                    
                    {/* Product details based on category */}
                    <div style={{
                      fontSize: '0.85rem',
                      color: theme.isDarkMode ? '#aaa' : '#666',
                      marginBottom: '12px',
                      flex: 1
                    }}>
                      {product.category === 'Screens' && (
                        <p>Screen Size: {product.screenSize}" • {product.controlMethod}</p>
                      )}
                      {product.category.includes('Imaging') && (
                        <p>Type: {product.type} • Range: {product.maxRange}</p>
                      )}
                      {product.category === 'Batteries' && (
                        <p>Capacity: {product.capacity} • Type: {product.type}</p>
                      )}
                      {product.category === 'Shuttles & Cases' && (
                        <p>Construction: {product.construction}</p>
                      )}
                      {product.category === 'Pole Mounts' && (
                        <p>Type: {product.poleType} • {product.bestFor}</p>
                      )}
                    </div>
                    
                    {/* Price and CTA */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'auto'
                    }}>
                      <div>
                        {(() => {
                          const productPricing = lowestPrices[product.id];
                          const hasLowestPrice = productPricing && productPricing.success && productPricing.price;

                          if (hasLowestPrice) {
                            return (
                              <div>
                                <div style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '700',
                                  color: theme.isDarkMode ? '#2ecc71' : '#27ae60'
                                }}>
                                  {formatPrice(productPricing.price)}
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: theme.isDarkMode ? '#888' : '#666'
                                }}>
                                  Best Price
                                </div>
                                {productPricing.msrpSavings && (
                                  <div style={{
                                    fontSize: '0.7rem',
                                    color: '#e67e22',
                                    fontWeight: '500',
                                    marginTop: '2px'
                                  }}>
                                    ↓ Save ${productPricing.msrpSavings.amount}
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            return (
                              <div>
                                <div style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '700',
                                  color: theme.isDarkMode ? '#888' : '#666'
                                }}>
                                  {formatPrice(product.msrp)}
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: theme.isDarkMode ? '#888' : '#666'
                                }}>
                                  {product.csvLookupId ? 'Loading...' : 'MSRP'}
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>
                      
                      <div style={{
                        backgroundColor: theme.isDarkMode ? '#3498db' : '#2980b9',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        Compare Prices
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Copy Link Toast */}
      {showCopyToast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: theme.isDarkMode ? '#2ecc71' : '#27ae60',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '600'
        }}>
          ✓ Link copied to clipboard!
        </div>
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '90%',
            maxHeight: '90%',
            position: 'relative',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <button
              onClick={() => setShowQRCode(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.colors.text,
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            <h3 style={{
              margin: '0 0 20px 0',
              color: theme.colors.text,
              fontSize: '1.2rem'
            }}>
              QR Code for Price Comparison Tool
            </h3>
            <img
              src={generateQRCodeUrl()}
              alt="QR Code for current page"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                border: '1px solid #ddd',
                borderRadius: '8px'
              }}
            />
            <p style={{
              margin: '15px 0 0 0',
              fontSize: '14px',
              color: theme.isDarkMode ? '#aaa' : '#666'
            }}>
              Scan with your phone to access this tool
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceComparisonLanding; 